/**
 * Navigation Shell Manager
 * Provides a browser-compatible communication channel to enable/disable the navigation shell
 */

import { internalLogger } from '../../interface/v1/clientOsLogger';

// Channel name for browser-compatible communication
const MULTI_BROWSER_CHANNEL_NAME = 'clientos-multi-browser-channel';

// Event types for the channel
export enum MultiChannelEvents {
  USER_LOGGED_IN = 'shell-web-user-logged-in'
}

// Interface for status change event data
export interface NavigationShellStatusEvent {
  type: MultiChannelEvents;
}

class MultiChannelEventManager {
  private channel: BroadcastChannel;
  private listeners: Map<
    string,
    Set<(event: NavigationShellStatusEvent) => void>
  > = new Map();

  constructor() {
    // Use BroadcastChannel for cross-tab communication
    try {
      this.channel = new BroadcastChannel(MULTI_BROWSER_CHANNEL_NAME);
      this.channel.onmessage = this.handleChannelMessage.bind(this);
    } catch (error) {
      internalLogger?.error(
        'BroadcastChannel not supported, falling back to local implementation',
        error
      );
    }
  }

  /**
   * Handle incoming channel messages
   */
  private handleChannelMessage(event: MessageEvent) {
    const data = event.data as NavigationShellStatusEvent;
    internalLogger?.log('Received message from channel:', data);
    this.notifyListeners(data);
  }

  /**
   * Notify all listeners
   */
  private notifyListeners(event: NavigationShellStatusEvent) {
    this.getListeners(event.type).forEach((listener) => listener(event));
  }

  /**
   * Get or create a set of listeners for a specific event type
   */
  private getListeners(
    type: MultiChannelEvents
  ): Set<(event: NavigationShellStatusEvent) => void> {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    return this.listeners.get(type)!;
  }
  /**
   * Add a listener for navigation shell status changes
   */
  subscribe(
    type: MultiChannelEvents,
    callback: (event: NavigationShellStatusEvent) => void
  ): () => void {
    this.getListeners(type).add(callback);

    // Return a function to remove the listener
    return () => {
      this.getListeners(type).delete(callback);
    };
  }

  publish(type: MultiChannelEvents, event: any): void {
    this.channel.postMessage({
      message: event,
      type: type
    });
  }
}

// Export a singleton instance
const multiChannelEventManager = new MultiChannelEventManager();
export default multiChannelEventManager;
